// @oasis-permission-inherits: /jerboa-codec-report (jerboa:view)
import { Link, createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useMemo } from "react";
import { z } from "zod";

import {
  InfiniteQueryPagination,
  useInfiniteQueryPagination,
} from "~/components/infinite-query-pagination";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { formatTimestamp } from "~/modules/jerboa-client-preview/formatters";
import {
  fetchJerboaCodecReportAnalysisQuery,
  fetchJerboaCodecReportsQuery,
} from "~/modules/jerboa-codec-report/queries";

const searchSchema = z.object({
  page: z.number().optional(),
  status: z.enum(["success", "failure"]).optional(),
  projectId: z.string().optional(),
  assetId: z.string().optional(),
  codec: z.string().optional(),
  pixelFormat: z.string().optional(),
  hardwareAcceleration: z.string().optional(),
  browserName: z.string().optional(),
  os: z.string().optional(),
});

export const Route = createFileRoute("/_dash/jerboa-codec-report/")({
  validateSearch: searchSchema,
  component: RouteComponent,
});

function RouteComponent() {
  const {
    page,
    status,
    projectId,
    assetId,
    codec,
    pixelFormat,
    hardwareAcceleration,
    browserName,
    os,
  } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const router = useRouter();
  const params = useMemo(
    () => ({
      status,
      projectId,
      assetId,
      codec,
      pixelFormat,
      hardwareAcceleration,
      browserName,
      os,
    }),
    [status, projectId, assetId, codec, pixelFormat, hardwareAcceleration, browserName, os]
  );

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, error } =
    fetchJerboaCodecReportsQuery.useInfiniteQuery({ params });

  const analysisParams = useMemo(() => ({ days: 30, ...params }), [params]);
  const { data: analysisData } = fetchJerboaCodecReportAnalysisQuery.useQuery({
    params: analysisParams,
  });

  const {
    currentPageIndex,
    currentPageData,
    totalPages,
    onPreviousPage,
    onNextPage,
    onPageChange,
  } = useInfiniteQueryPagination({
    page,
    data,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    navigateToPage: (nextPage) => navigate({ search: { ...params, page: nextPage } }),
  });

  const topCodecs = analysisData?.analysis.byCodec?.slice(0, 6) ?? [];
  const topStatuses = analysisData?.analysis.byStatus?.slice(0, 6) ?? [];
  const topAcceleration = analysisData?.analysis.byHardwareAcceleration?.slice(0, 6) ?? [];
  const topBrowsers = analysisData?.analysis.byBrowser?.slice(0, 6) ?? [];

  const updateFilters = (next: Partial<typeof params>) => {
    navigate({ search: { ...params, ...next, page: 1 } });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Client Codecs</h1>
        {totalPages > 0 && (
          <InfiniteQueryPagination
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onPageChange={onPageChange}
            currentPageIndex={currentPageIndex}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            totalPages={totalPages}
          />
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <StatusButton
          label="All"
          selected={!status}
          onClick={() => updateFilters({ status: undefined })}
        />
        <StatusButton
          label="Success"
          selected={status === "success"}
          onClick={() => updateFilters({ status: "success" })}
        />
        <StatusButton
          label="Failure"
          selected={status === "failure"}
          onClick={() => updateFilters({ status: "failure" })}
        />
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <AnalysisGroup title="Status" groups={topStatuses} />
        <AnalysisGroup title="Codecs" groups={topCodecs} />
        <AnalysisGroup title="Acceleration" groups={topAcceleration} />
        <AnalysisGroup title="Browsers" groups={topBrowsers} />
      </div>

      {error && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          <p className="font-medium">Failed to load codec reports</p>
          <pre className="mt-2 whitespace-pre-wrap text-xs">{error.message}</pre>
        </div>
      )}

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-md" />
          ))}
        </div>
      )}

      {!isLoading && currentPageData && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Created At</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Codec</TableHead>
              <TableHead>Profile</TableHead>
              <TableHead>Pixel Format</TableHead>
              <TableHead>Acceleration</TableHead>
              <TableHead>Decode</TableHead>
              <TableHead>Browser</TableHead>
              <TableHead>OS</TableHead>
              <TableHead>Project</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentPageData.page.map((report) => (
              <TableRow
                key={report.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() =>
                  router.navigate({
                    to: "/jerboa-codec-report/$id",
                    params: { id: report.id },
                    search: { page: currentPageIndex + 1, ...params },
                  })
                }
              >
                <TableCell>{formatTimestamp(report.createdAt)}</TableCell>
                <TableCell>
                  <Badge variant={report.status === "failure" ? "destructive" : "outline"}>
                    {report.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="font-mono text-xs">{report.codec.family ?? "-"}</div>
                </TableCell>
                <TableCell>{report.codec.profile ?? "-"}</TableCell>
                <TableCell>
                  <Badge variant="outline">{report.codec.pixelFormat ?? "unknown"}</Badge>
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {report.hardwareAcceleration ?? "-"}
                </TableCell>
                <TableCell className="text-xs">
                  {typeof report.decodeDurationMs === "number"
                    ? `${Math.round(report.decodeDurationMs)}ms`
                    : "-"}
                </TableCell>
                <TableCell>{report.browserInfo?.browserName ?? "-"}</TableCell>
                <TableCell className="text-xs">{report.browserInfo?.os ?? "-"}</TableCell>
                <TableCell className="font-mono text-xs">
                  {report.projectId ? (
                    <Link
                      to="/projects/$projectId"
                      params={{ projectId: report.projectId }}
                      className="text-blue-600 hover:underline"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {report.projectId}
                    </Link>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
            ))}
            {currentPageData.page.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-muted-foreground">
                  No codec reports found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function StatusButton({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <Button type="button" size="sm" variant={selected ? "default" : "outline"} onClick={onClick}>
      {label}
    </Button>
  );
}

function AnalysisGroup({
  title,
  groups,
}: {
  title: string;
  groups: Array<{ key: string; count: number }>;
}) {
  return (
    <div className="rounded-md border bg-muted/20 p-4">
      <p className="mb-3 text-sm font-medium">{title}</p>
      <div className="space-y-2">
        {groups.length === 0 && <p className="text-sm text-muted-foreground">No data</p>}
        {groups.map((group) => (
          <div key={group.key} className="flex items-center justify-between gap-3 text-sm">
            <span className="truncate font-mono text-xs">{group.key || "unknown"}</span>
            <Badge variant="outline">{group.count}</Badge>
          </div>
        ))}
      </div>
    </div>
  );
}
